import React, { useEffect, useState } from 'react';

/**
 * Emoji-forward building blocks for the /start Business Assessment
 * (ExistingConditions). Purely presentational — the page still owns every piece
 * of form state, the Firestore saves, and the section gating rules. Panels are
 * translucent so the shared PortalBackground reads through them, matching the
 * Start Dashboard.
 */

/**
 * The 1–5 star self-rating scale used by every department section. Labels are
 * the long-standing assessment copy and the stored value is still 1–5.
 */
export const RATING_LEVELS = [
  { value: 1, label: 'Needs Drastic Improvement' },
  { value: 2, label: 'Not Great' },
  { value: 3, label: "We're Getting By" },
  { value: 4, label: 'Pretty Good' },
  { value: 5, label: 'Exceptional' },
];

export const ratingLabel = (value) =>
  RATING_LEVELS.find((level) => level.value === value)?.label || 'Please select a rating';

/** Trade emojis for the subcontractor focus checklist. */
export const SUBCONTRACTOR_FOCUS_EMOJI = {
  'Framing': '🪚',
  'Plumbing': '🚰',
  'Electrical': '⚡',
  'HVAC': '❄️',
  'Drywall': '🧱',
  'Insulation': '🧣',
  'Painting': '🎨',
  'Roofing': '🛖',
  'Flooring': '🪵',
  'Tile': '🔲',
  'Siding': '🏡',
  'Cabinetry & Millwork': '🗄️',
  'Concrete & Foundations': '🏗️',
  'Landscaping': '🌳',
  'Hardscaping': '🪨',
  'Other': '➕',
};

/** Emojis for the home-improvement focus checklist. */
export const HOME_IMPROVEMENT_FOCUS_EMOJI = {
  'Handyman Services': '🧰',
  'Kitchen Remodels': '🍳',
  'Bathroom Remodels': '🛁',
  'Basement Finishing': '🪜',
  'Decks & Patios': '🪑',
  'Pools': '🏊',
  'Window & Door': '🪟',
  'Siding & Exterior': '🏡',
  'Metal Fabrication': '⚙️',
};

const PANEL_SHELL =
  'rounded-2xl border border-gray-200 dark:border-gray-700 bg-white/85 dark:bg-gray-800/85 backdrop-blur-sm shadow-sm';

/** Frosted card that groups one question (or one cluster of related fields). */
export const AssessmentPanel = ({ emoji, title, description, children, className = '' }) => (
  <section className={`${PANEL_SHELL} p-5 sm:p-6 ${className}`}>
    {(emoji || title || description) && (
      <header className="text-center mb-5">
        {emoji && <div className="text-3xl leading-none mb-2" aria-hidden="true">{emoji}</div>}
        {title && (
          <h4 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">{title}</h4>
        )}
        {description && (
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{description}</p>
        )}
      </header>
    )}
    {children}
  </section>
);

const OPTION_SIZES = {
  xl: { pad: 'p-5 sm:p-10', emoji: 'text-5xl sm:text-7xl', label: 'text-base sm:text-2xl', gap: 'gap-3 sm:gap-4' },
  lg: { pad: 'p-6 sm:p-8', emoji: 'text-5xl', label: 'text-lg sm:text-xl', gap: 'gap-3' },
  md: { pad: 'p-5', emoji: 'text-4xl', label: 'text-base sm:text-lg', gap: 'gap-2.5' },
  sm: { pad: 'p-4', emoji: 'text-3xl', label: 'text-sm sm:text-base', gap: 'gap-2' },
};

/**
 * A single selectable tile: big emoji, label, optional supporting line. Used for
 * every "pick one" and "pick many" question in the assessment.
 */
export const OptionCard = ({
  emoji,
  label,
  description,
  selected = false,
  onClick,
  size = 'md',
  className = '',
  children,
}) => {
  const s = OPTION_SIZES[size] || OPTION_SIZES.md;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={`group relative flex h-full w-full flex-col items-center justify-center rounded-2xl border-2 text-center transition-all duration-200 ${s.pad} ${s.gap} ${
        selected
          ? 'border-red-500 bg-red-50 dark:bg-red-900/25 shadow-lg shadow-red-500/10 -translate-y-0.5'
          : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/70 hover:-translate-y-0.5 hover:border-red-300 dark:hover:border-red-500/60 hover:shadow-md'
      } ${className}`}
    >
      <span
        className={`absolute top-3 right-3 flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold transition-all duration-200 ${
          selected
            ? 'scale-100 bg-red-500 text-white opacity-100'
            : 'scale-75 bg-gray-200 text-transparent opacity-0 dark:bg-gray-700'
        }`}
        aria-hidden="true"
      >
        ✓
      </span>
      {emoji && (
        <span
          className={`${s.emoji} leading-none transition-transform duration-200 group-hover:scale-110`}
          aria-hidden="true"
        >
          {emoji}
        </span>
      )}
      <span className={`${s.label} font-bold text-gray-900 dark:text-white`}>{label}</span>
      {description && (
        <span className="text-xs sm:text-sm leading-snug text-gray-500 dark:text-gray-400">
          {description}
        </span>
      )}
      {children}
    </button>
  );
};

/**
 * Self-contained "?" bubble. Keeps its own open state so the page no longer has
 * to carry a boolean per tooltip. `align="left"` anchors the bubble to the tip's
 * left edge instead of centring it — the centred version overflows (and gets
 * clipped by the form card) when the tip sits near a panel's left edge.
 */
export const InfoTip = ({ text, label = 'More info', align = 'center' }) => {
  const [open, setOpen] = useState(false);
  return (
    <span className="relative inline-flex">
      <button
        type="button"
        aria-label={label}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((prev) => !prev);
        }}
        className="flex h-5 w-5 items-center justify-center rounded-full bg-gray-200 text-[11px] font-bold text-gray-600 transition-colors hover:bg-red-500 hover:text-white dark:bg-gray-700 dark:text-gray-300"
      >
        ?
      </button>
      {open && (
        <span
          className={`absolute bottom-full z-50 mb-2 w-56 rounded-xl border border-gray-200 bg-white p-3 text-xs font-normal leading-relaxed text-gray-700 shadow-xl dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 ${
            align === 'left' ? 'left-0 text-left' : 'left-1/2 -translate-x-1/2'
          }`}
          role="tooltip"
        >
          {text}
        </span>
      )}
    </span>
  );
};

/**
 * Yes/no capability tile ("Do you have SOPs?"). Selected means yes; clicking a
 * selected tile clears it back to no, which is how the original cards behaved.
 */
export const HaveItCard = ({ emoji, label, info, selected, onClick }) => (
  <div className="relative h-full">
    <OptionCard
      emoji={emoji}
      label={label}
      selected={selected}
      onClick={onClick}
      size="md"
      className="pb-10"
    />
    {info && (
      <span className="absolute top-3 left-3 z-10">
        <InfoTip text={info} label={`About ${label}`} align="left" />
      </span>
    )}
    <span
      className={`pointer-events-none absolute inset-x-0 bottom-3 text-center text-xs font-semibold ${
        selected ? 'text-red-600 dark:text-red-400' : 'text-gray-400 dark:text-gray-500'
      }`}
    >
      {selected ? 'Yes' : 'Not yet'}
    </span>
  </div>
);

const StarGlyph = ({ filled }) => (
  <svg
    viewBox="0 0 24 24"
    className={`h-9 w-9 transition-all duration-200 sm:h-11 sm:w-11 ${
      filled
        ? 'fill-amber-400 drop-shadow-[0_2px_6px_rgba(251,191,36,0.45)]'
        : 'fill-gray-200 dark:fill-gray-600'
    }`}
    aria-hidden="true"
  >
    <path d="M12 2.6l2.9 5.9 6.5.95-4.7 4.58 1.11 6.47L12 17.45l-5.81 3.05 1.11-6.47L2.6 9.45l6.5-.95L12 2.6z" />
  </svg>
);

/**
 * The 1–5 star scale. Hovering previews the fill; `value` of 0 means nothing
 * picked yet.
 */
export const RatingScale = ({ value = 0, onChange }) => {
  const [hovered, setHovered] = useState(0);
  const active = hovered || value;

  return (
    <div>
      <div
        className="flex items-center justify-center gap-1 sm:gap-2"
        onMouseLeave={() => setHovered(0)}
      >
        {RATING_LEVELS.map((level) => (
          <button
            key={level.value}
            type="button"
            onClick={() => onChange(level.value)}
            onMouseEnter={() => setHovered(level.value)}
            aria-label={`${level.value} of 5 — ${level.label}`}
            aria-pressed={value === level.value}
            className="rounded-full p-1 transition-transform duration-150 hover:scale-125 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
          >
            <StarGlyph filled={level.value <= active} />
          </button>
        ))}
      </div>

      <p
        className={`mt-3 text-center text-base font-bold ${
          value ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-500'
        }`}
      >
        {ratingLabel(value)}
      </p>
    </div>
  );
};

const dialTone = (pct) => {
  if (pct >= 75) return { ring: '#22c55e', text: 'text-green-600 dark:text-green-400' };
  if (pct >= 50) return { ring: '#eab308', text: 'text-yellow-600 dark:text-yellow-400' };
  return { ring: '#ef4444', text: 'text-red-600 dark:text-red-400' };
};

/**
 * Circular 0–100% dial with a click-to-type centre and a slider underneath.
 * Owns its own edit state so each caller just passes value + onChange.
 */
export const PercentDial = ({ value = 0, onChange, darkMode = false }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const pct = Math.min(100, Math.max(0, Number(value) || 0));
  const tone = dialTone(pct);
  const circumference = 2 * Math.PI * 45;

  const commit = () => {
    const parsed = parseInt(draft, 10);
    onChange(Math.min(100, Math.max(0, Number.isNaN(parsed) ? 0 : parsed)));
    setEditing(false);
  };

  return (
    <div className="flex flex-col items-center space-y-4">
      <div className="relative flex h-44 w-44 items-center justify-center">
        <svg className="h-full w-full -rotate-90" viewBox="0 0 100 100">
          <circle cx="50" cy="50" r="45" fill="none" stroke={darkMode ? '#374151' : '#e5e7eb'} strokeWidth="8" />
          <circle
            cx="50"
            cy="50"
            r="45"
            fill="none"
            stroke={tone.ring}
            strokeWidth="8"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={circumference * (1 - pct / 100)}
            className="transition-all duration-300"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          {editing ? (
            <div className="flex items-baseline justify-center">
              <input
                type="number"
                min="0"
                max="100"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onBlur={commit}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') e.target.blur();
                  if (e.key === 'Escape') setEditing(false);
                }}
                autoFocus
                className={`w-20 rounded border-2 border-transparent bg-transparent text-center text-4xl font-bold focus:border-red-500 focus:outline-none ${tone.text}`}
                style={{ MozAppearance: 'textfield' }}
              />
              <span className="text-xl font-semibold text-gray-400">%</span>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => {
                setDraft(String(pct));
                setEditing(true);
              }}
              className="flex items-baseline transition-opacity hover:opacity-70"
              title="Click to type an exact number"
            >
              <span className={`text-5xl font-extrabold ${tone.text}`}>{pct}</span>
              <span className="text-xl font-semibold text-gray-400">%</span>
            </button>
          )}
        </div>
      </div>

      <div className="w-full max-w-xs">
        <input
          type="range"
          min="0"
          max="100"
          value={pct}
          onChange={(e) => onChange(parseInt(e.target.value, 10))}
          className="w-full cursor-pointer appearance-none"
        />
        <div className="mt-1 flex items-center justify-between text-[11px] font-medium text-gray-400 dark:text-gray-500">
          <span>0%</span>
          <span>100%</span>
        </div>
      </div>
    </div>
  );
};

const FIELD_INPUT_CLASS =
  'block w-full rounded-xl border-2 border-gray-200 bg-white py-3 text-center text-base font-semibold text-gray-900 shadow-sm transition-all duration-200 placeholder:font-normal placeholder:text-gray-400 focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:focus:ring-red-800';

const FieldShell = ({ emoji, label, children }) => (
  <div className="rounded-2xl border-2 border-gray-200 bg-white p-4 transition-colors focus-within:border-red-400 dark:border-gray-700 dark:bg-gray-800/70">
    <label className="mb-2 flex items-center justify-center gap-2 text-center text-sm font-semibold text-gray-700 dark:text-gray-200">
      {emoji && <span className="text-lg leading-none" aria-hidden="true">{emoji}</span>}
      {label}
    </label>
    {children}
  </div>
);

/** Labelled money / percentage input tile. */
export const FieldCard = ({
  emoji,
  label,
  name,
  value,
  onChange,
  placeholder,
  prefix,
  suffix,
  type = 'text',
  min,
  children,
}) => (
  <FieldShell emoji={emoji} label={label}>
    <div className="relative">
      {prefix && (
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 font-semibold text-gray-400">
          {prefix}
        </span>
      )}
      <input
        type={type}
        name={name}
        min={min}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={`${FIELD_INPUT_CLASS} ${prefix ? 'pl-8' : 'pl-3'} ${suffix ? 'pr-8' : 'pr-3'}`}
      />
      {suffix && (
        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 font-semibold text-gray-400">
          {suffix}
        </span>
      )}
    </div>
    {children}
  </FieldShell>
);

/** M = millions, K = thousands — the two scales contractors quote revenue in. */
const UNIT_MULTIPLIER = { K: 1000, M: 1000000 };

const toStoredAmount = (shown, unit) => {
  const parsed = parseFloat(shown);
  return Number.isFinite(parsed) ? String(Math.round(parsed * UNIT_MULTIPLIER[unit])) : '';
};

/**
 * Money tile where the lead types a small number ("1.5") and picks the scale
 * with a tappable M/K pill. The value handed back to the form is always the full
 * dollar amount, so everything downstream (analysis pages, lead sync) keeps
 * parsing it exactly as before.
 */
export const UnitAmountField = ({
  emoji,
  label,
  name,
  value,
  onChange,
  placeholder,
  defaultUnit = 'M',
  children,
}) => {
  const [unit, setUnit] = useState(defaultUnit);
  const [shown, setShown] = useState('');

  // Re-derive what's on screen whenever the stored amount arrives from
  // somewhere other than this input (Firestore hydrating the form after mount).
  useEffect(() => {
    const stored = parseFloat(String(value ?? '').replace(/[^0-9.]/g, ''));
    if (!Number.isFinite(stored)) {
      setShown('');
      return;
    }
    if (toStoredAmount(shown, unit) === String(Math.round(stored))) return;
    setShown(String(Number((stored / UNIT_MULTIPLIER[unit]).toFixed(6))));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, unit]);

  // Callers still hand us a plain input handler, so hand them back a synthetic
  // event carrying the full dollar amount.
  const emit = (nextShown, nextUnit) =>
    onChange({ target: { name, value: toStoredAmount(nextShown, nextUnit) } });

  const nextUnit = unit === 'M' ? 'K' : 'M';

  return (
    <FieldShell emoji={emoji} label={label}>
      <div className="relative">
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 font-semibold text-gray-400">
          $
        </span>
        <input
          type="text"
          inputMode="decimal"
          name={name}
          value={shown}
          onChange={(e) => {
            setShown(e.target.value);
            emit(e.target.value, unit);
          }}
          placeholder={placeholder}
          className={`${FIELD_INPUT_CLASS} pl-8 pr-12`}
        />
        <button
          type="button"
          onClick={() => {
            emit(shown, nextUnit);
            setUnit(nextUnit);
          }}
          title={`Switch to ${nextUnit === 'M' ? 'millions' : 'thousands'}`}
          aria-label={`Unit: ${unit === 'M' ? 'millions' : 'thousands'}. Switch to ${
            nextUnit === 'M' ? 'millions' : 'thousands'
          }.`}
          className="absolute right-2 top-1/2 flex h-7 w-8 -translate-y-1/2 items-center justify-center rounded-lg bg-gray-100 text-sm font-bold text-gray-600 transition-colors hover:bg-red-500 hover:text-white dark:bg-gray-600 dark:text-gray-200"
        >
          {unit}
        </button>
      </div>
      {children}
    </FieldShell>
  );
};

/** Dashed drop-zone style file picker used for the finance statements. */
export const UploadCard = ({ id, emoji, label, uploading, fileName, uploaded, accept, onChange }) => (
  <div className="mt-4">
    <input type="file" id={id} accept={accept} onChange={onChange} className="hidden" />
    <label
      htmlFor={id}
      className={`flex w-full cursor-pointer items-center justify-center gap-2 rounded-xl border-2 border-dashed px-3 py-2.5 text-sm font-medium transition-all duration-200 ${
        uploading
          ? 'border-gray-400 bg-gray-100 text-gray-500 dark:bg-gray-700'
          : uploaded
            ? 'border-green-500 bg-green-50 text-green-700 hover:border-green-600 dark:bg-green-900/20 dark:text-green-300'
            : 'border-gray-300 text-gray-600 hover:border-red-400 hover:text-red-600 dark:border-gray-600 dark:text-gray-300 dark:hover:border-red-500'
      }`}
    >
      <span className="text-base leading-none" aria-hidden="true">
        {uploading ? '⏳' : uploaded ? '✅' : emoji}
      </span>
      <span className="truncate">
        {uploading ? 'Uploading…' : uploaded ? fileName || `${label} uploaded` : `Upload ${label}`}
      </span>
    </label>
  </div>
);

const GRADE_TONE = {
  A: 'border-green-500 bg-green-500 text-white shadow-green-500/25',
  B: 'border-emerald-400 bg-emerald-400 text-white shadow-emerald-400/25',
  C: 'border-yellow-500 bg-yellow-500 text-white shadow-yellow-500/25',
  D: 'border-orange-500 bg-orange-500 text-white shadow-orange-500/25',
  F: 'border-red-600 bg-red-600 text-white shadow-red-600/25',
};

/** A–F report-card picker. Clicking the active grade clears it, as before. */
export const GradePicker = ({ value, onChange, grades = ['A', 'B', 'C', 'D', 'F'] }) => (
  <div className="flex items-center justify-center gap-2 sm:gap-3">
    {grades.map((grade) => {
      const selected = value === grade;
      return (
        <button
          key={grade}
          type="button"
          onClick={() => onChange(selected ? '' : grade)}
          aria-pressed={selected}
          className={`flex h-12 w-12 items-center justify-center rounded-full border-2 text-xl font-extrabold transition-all duration-200 sm:h-14 sm:w-14 sm:text-2xl ${
            selected
              ? `${GRADE_TONE[grade]} scale-110 shadow-lg`
              : 'border-gray-200 bg-white text-gray-400 hover:border-red-300 hover:text-gray-600 dark:border-gray-700 dark:bg-gray-800/70 dark:text-gray-500 dark:hover:text-gray-300'
          }`}
        >
          {grade}
        </button>
      );
    })}
  </div>
);

/** Inline validation callout. */
export const AssessmentAlert = ({ children }) => (
  <div className="mt-4 flex items-center justify-center gap-2 rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-sm font-medium text-red-800 dark:border-red-700 dark:bg-red-900/20 dark:text-red-200">
    <span aria-hidden="true">⚠️</span>
    {children}
  </div>
);

/** Green "…Assessment Completed!" confirmation strip. */
export const CompletedNote = ({ children }) => (
  <div className="flex items-center justify-center gap-2 rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm font-semibold text-green-700 dark:border-green-700 dark:bg-green-900/20 dark:text-green-300">
    <span aria-hidden="true">✅</span>
    {children}
  </div>
);

/**
 * "Question 3 of 7" header plus the bar and step pips. Percent is derived from
 * the step so the number and the bar can never disagree.
 */
export const AssessmentProgress = ({ step, total, sectionTitle, sectionEmoji, percent: percentOverride }) => {
  const safeTotal = Math.max(1, total || 1);
  const percent = Number.isFinite(percentOverride)
    ? percentOverride
    : Math.round((Math.min(step, safeTotal) / safeTotal) * 100);
  return (
    <div className={`${PANEL_SHELL} p-5 sm:p-6`}>
      <div className="mb-3 flex flex-wrap items-center justify-center gap-x-3 gap-y-2">
        <span className="inline-flex items-center gap-2 rounded-full bg-red-50 px-3 py-1 text-sm font-bold text-red-700 dark:bg-red-900/30 dark:text-red-300">
          <span aria-hidden="true">{sectionEmoji}</span>
          Question {Math.min(step, safeTotal)} of {safeTotal}
        </span>
        <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">{sectionTitle}</span>
      </div>

      <div className="relative h-3 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
        <div
          className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-red-500 via-red-600 to-red-700 transition-all duration-700 ease-out"
          style={{ width: `${percent}%` }}
        />
      </div>

      <div className="mt-3 flex items-center justify-center gap-1.5">
        {Array.from({ length: safeTotal }).map((_, index) => (
          <span
            key={index}
            className={`h-1.5 rounded-full transition-all duration-300 ${
              index < step ? 'w-6 bg-red-500' : 'w-3 bg-gray-300 dark:bg-gray-600'
            }`}
          />
        ))}
      </div>

      <p className="mt-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400">
        {percent}% complete
      </p>
    </div>
  );
};
